import { useRef, useEffect, useCallback } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";

const PROP_GEOMETRIES = {
  table:     { w: 1.2, h: 0.75, d: 0.8, color: "#8B6914" },
  chair:     { w: 0.5, h: 0.9, d: 0.5, color: "#6B4226" },
  sofa:      { w: 2.0, h: 0.8, d: 0.9, color: "#4A5568" },
  bed:       { w: 2.0, h: 0.5, d: 1.6, color: "#E2D5C3" },
  tv:        { w: 1.2, h: 0.7, d: 0.05, color: "#1A1A2E" },
  shelf:     { w: 1.0, h: 1.8, d: 0.35, color: "#A0522D" },
  lamp:      { w: 0.3, h: 1.5, d: 0.3, color: "#DAA520" },
  door:      { w: 0.9, h: 2.1, d: 0.08, color: "#654321" },
  window:    { w: 1.2, h: 1.0, d: 0.05, color: "#87CEEB" },
  desk:      { w: 1.4, h: 0.75, d: 0.7, color: "#5C4033" },
  wardrobe:  { w: 1.2, h: 2.0, d: 0.6, color: "#3E2723" },
  rug:       { w: 2.0, h: 0.02, d: 1.5, color: "#8B0000" },
  plant:     { w: 0.4, h: 1.0, d: 0.4, color: "#228B22" },
  custom:    { w: 1.0, h: 1.0, d: 1.0, color: "#888888" },
};

const CHARACTER_COLORS = [
  "#E6A23C", "#409EFF", "#67C23A", "#F56C6C", "#909399",
  "#E040FB", "#00BCD4", "#FF9800"
];

// Pose presets: rotation angles in radians for each limb pivot
const POSE_PRESETS = {
  standing:    { leftArm: 0, rightArm: 0, leftLeg: 0, rightLeg: 0, bodyY: 0 },
  sitting:     { leftArm: -0.3, rightArm: -0.3, leftLeg: -Math.PI / 2, rightLeg: -Math.PI / 2, bodyY: -0.32 },
  walking:     { leftArm: 0.5, rightArm: -0.5, leftLeg: -0.4, rightLeg: 0.4, bodyY: 0 },
  pointing:    { leftArm: 0, rightArm: -1.3, leftLeg: 0, rightLeg: 0, bodyY: 0 },
  arms_crossed:{ leftArm: -1.2, rightArm: -1.2, leftLeg: 0, rightLeg: 0, bodyY: 0 },
  hands_hips:  { leftArm: -0.7, rightArm: -0.7, leftLeg: 0.05, rightLeg: -0.05, bodyY: 0 },
  gesturing:   { leftArm: -0.2, rightArm: -1.6, leftLeg: 0, rightLeg: 0, bodyY: 0 },
  action:      { leftArm: -0.8, rightArm: 0.6, leftLeg: -0.3, rightLeg: 0.3, bodyY: 0 },
};

function createPropMesh(prop) {
  const def = PROP_GEOMETRIES[prop.type] || PROP_GEOMETRIES.custom;
  const dims = prop.dimensions || { w: def.w, h: def.h, d: def.d };
  const geo = new THREE.BoxGeometry(dims.w, dims.h, dims.d);
  const mat = new THREE.MeshStandardMaterial({
    color: prop.color || def.color,
    roughness: 0.7,
    metalness: 0.1,
  });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.position.set(
    prop.position?.x || 0,
    (prop.position?.y ?? dims.h / 2),
    prop.position?.z || 0
  );
  if (prop.rotation) {
    mesh.rotation.set(prop.rotation.x || 0, prop.rotation.y || 0, prop.rotation.z || 0);
  }
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  mesh.userData = { id: prop.id, type: "prop", propType: prop.type, label: prop.label };

  // Label sprite
  const canvas = document.createElement("canvas");
  canvas.width = 256; canvas.height = 64;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "rgba(0,0,0,0.6)";
  ctx.roundRect(0, 0, 256, 64, 8); ctx.fill();
  ctx.fillStyle = "#ffffff";
  ctx.font = "bold 24px sans-serif";
  ctx.textAlign = "center";
  ctx.fillText(prop.label || prop.type, 128, 40);
  const tex = new THREE.CanvasTexture(canvas);
  const spriteMat = new THREE.SpriteMaterial({ map: tex, transparent: true });
  const sprite = new THREE.Sprite(spriteMat);
  sprite.position.y = dims.h / 2 + 0.3;
  sprite.scale.set(1.2, 0.3, 1);
  mesh.add(sprite);

  return mesh;
}

function createCharacterMesh(char, index) {
  const color = CHARACTER_COLORS[index % CHARACTER_COLORS.length];
  const h = char.height || 1.7;
  const pose = POSE_PRESETS[char.pose || "standing"] || POSE_PRESETS.standing;
  const group = new THREE.Group();

  // Body (torso)
  const bodyGeo = new THREE.CylinderGeometry(0.18, 0.22, h * 0.45, 12);
  const bodyMat = new THREE.MeshStandardMaterial({ color, roughness: 0.5 });
  const body = new THREE.Mesh(bodyGeo, bodyMat);
  body.position.y = h * 0.45 + pose.bodyY;
  group.add(body);

  // Head
  const headGeo = new THREE.SphereGeometry(0.12, 16, 12);
  const headMat = new THREE.MeshStandardMaterial({ color: "#F5D0A9", roughness: 0.6 });
  const head = new THREE.Mesh(headGeo, headMat);
  head.position.y = h * 0.72 + pose.bodyY;
  group.add(head);

  // Legs with hip pivots
  const legLength = h * 0.42;
  const legOffsets = [
    { x: -0.08, rot: pose.leftLeg },
    { x: 0.08, rot: pose.rightLeg },
  ];
  for (const leg of legOffsets) {
    const pivot = new THREE.Group();
    pivot.position.set(leg.x, h * 0.22 + Math.min(0, pose.bodyY), 0); // hip joint
    pivot.rotation.x = leg.rot;
    const legGeo = new THREE.CylinderGeometry(0.06, 0.06, legLength, 8);
    const legMat = new THREE.MeshStandardMaterial({ color: "#2C3E50", roughness: 0.8 });
    const legMesh = new THREE.Mesh(legGeo, legMat);
    legMesh.position.y = -legLength / 2 + 0.02; // offset from pivot
    pivot.add(legMesh);
    group.add(pivot);
  }

  // Arms with shoulder pivots
  const armLength = h * 0.35;
  const armOffsets = [
    { x: -0.25, rot: pose.leftArm },
    { x: 0.25, rot: pose.rightArm },
  ];
  for (const arm of armOffsets) {
    const pivot = new THREE.Group();
    pivot.position.set(arm.x, h * 0.65 + pose.bodyY, 0); // shoulder joint
    pivot.rotation.x = arm.rot;
    const armGeo = new THREE.CylinderGeometry(0.04, 0.04, armLength, 8);
    const armMat = new THREE.MeshStandardMaterial({ color, roughness: 0.5 });
    const armMesh = new THREE.Mesh(armGeo, armMat);
    armMesh.position.y = -armLength / 2; // hang from pivot
    pivot.add(armMesh);
    group.add(pivot);
  }

  group.position.set(char.position?.x || 0, 0, char.position?.z || 0);
  group.rotation.y = char.rotation || 0;
  group.userData = { id: char.character_id, type: "character", name: char.character_name };

  // Name label
  const canvas = document.createElement("canvas");
  canvas.width = 256; canvas.height = 64;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = color;
  ctx.roundRect(0, 0, 256, 64, 8); ctx.fill();
  ctx.fillStyle = "#000";
  ctx.font = "bold 22px sans-serif";
  ctx.textAlign = "center";
  ctx.fillText(char.character_name || "Character", 128, 40);
  const tex = new THREE.CanvasTexture(canvas);
  const spriteMat = new THREE.SpriteMaterial({ map: tex, transparent: true });
  const sprite = new THREE.Sprite(spriteMat);
  sprite.position.y = h + 0.3 + pose.bodyY;
  sprite.scale.set(1.2, 0.3, 1);
  group.add(sprite);

  group.traverse(c => { if (c.isMesh) c.castShadow = true; });
  return group;
}

/**
 * SceneBuilder3D — Simple click-to-select, drag-to-move, click-floor-to-place
 */
export default function SceneBuilder3D({
  layout,
  onLayoutChange,
  onSelectObject,
  selectedId,
  onFloorClick,
}) {
  const containerRef = useRef(null);
  const threeRef = useRef(null);
  const propsGroupRef = useRef(null);
  const charsGroupRef = useRef(null);
  const dragRef = useRef({ active: false, object: null, offset: new THREE.Vector3() });
  const floorPlane = useRef(new THREE.Plane(new THREE.Vector3(0, 1, 0), 0));

  // Initialize scene
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x1a1a2e);

    const camera = new THREE.PerspectiveCamera(
      50, container.clientWidth / container.clientHeight, 0.1, 100
    );
    camera.position.set(0, 4, 7);

    const renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true });
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    container.appendChild(renderer.domElement);

    const orbit = new OrbitControls(camera, renderer.domElement);
    orbit.enableDamping = true;
    orbit.dampingFactor = 0.05;
    orbit.target.set(0, 0, 0);
    orbit.maxPolarAngle = Math.PI * 0.48;

    const propsGroup = new THREE.Group(); propsGroup.name = "props";
    scene.add(propsGroup); propsGroupRef.current = propsGroup;
    const charsGroup = new THREE.Group(); charsGroup.name = "characters";
    scene.add(charsGroup); charsGroupRef.current = charsGroup;

    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();
    const intersectPoint = new THREE.Vector3();

    // --- Pointer interactions: click to select, drag to move, click floor to place ---
    let pointerDownPos = { x: 0, y: 0 };
    let pointerDownTime = 0;

    const getMouseNDC = (e) => {
      const rect = renderer.domElement.getBoundingClientRect();
      mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    };

    const findHitObject = () => {
      raycaster.setFromCamera(mouse, camera);
      const allObjects = [...propsGroup.children, ...charsGroup.children];
      const meshes = [];
      allObjects.forEach(obj => obj.traverse(child => { if (child.isMesh) meshes.push(child); }));
      const hits = raycaster.intersectObjects(meshes, false);
      if (hits.length > 0) {
        let target = hits[0].object;
        while (target && !target.userData?.id) target = target.parent;
        return target;
      }
      return null;
    };

    const getFloorIntersection = () => {
      raycaster.setFromCamera(mouse, camera);
      raycaster.ray.intersectPlane(floorPlane.current, intersectPoint);
      return intersectPoint;
    };

    const onPointerDown = (e) => {
      if (e.button !== 0) return; // left click only
      getMouseNDC(e);
      pointerDownPos = { x: e.clientX, y: e.clientY };
      pointerDownTime = Date.now();

      const hit = findHitObject();
      if (hit && hit.userData?.id) {
        // Start dragging this object
        dragRef.current.active = true;
        dragRef.current.object = hit;
        orbit.enabled = false;

        // Calculate offset between object position and floor intersection
        const floorPt = getFloorIntersection();
        dragRef.current.offset.copy(hit.position).sub(new THREE.Vector3(floorPt.x, 0, floorPt.z));
      }
    };

    const onPointerMove = (e) => {
      if (!dragRef.current.active || !dragRef.current.object) return;
      getMouseNDC(e);
      const floorPt = getFloorIntersection();
      const obj = dragRef.current.object;
      const off = dragRef.current.offset;

      obj.position.x = floorPt.x + off.x;
      obj.position.z = floorPt.z + off.z;
      // Keep y unchanged (don't drop through floor)
    };

    const onPointerUp = (e) => {
      const wasDragging = dragRef.current.active;
      const draggedObj = dragRef.current.object;
      const dist = Math.hypot(e.clientX - pointerDownPos.x, e.clientY - pointerDownPos.y);
      const elapsed = Date.now() - pointerDownTime;
      const wasClick = dist < 5 && elapsed < 300;

      if (wasDragging && draggedObj) {
        // Emit position change
        if (onLayoutChange && dist > 3) {
          const pos = draggedObj.position;
          onLayoutChange({
            type: draggedObj.userData.type,
            id: draggedObj.userData.id,
            position: { x: +pos.x.toFixed(2), y: +pos.y.toFixed(2), z: +pos.z.toFixed(2) },
          });
        }
        // If it was a click (not drag), just select it
        if (wasClick && onSelectObject) {
          onSelectObject(draggedObj.userData);
        }
      } else if (wasClick) {
        // Clicked empty space — check if floor was hit
        getMouseNDC(e);
        const hit = findHitObject();
        if (!hit) {
          // Deselect or trigger floor click for placing props
          const floorPt = getFloorIntersection();
          if (onFloorClick) {
            onFloorClick({ x: +floorPt.x.toFixed(2), z: +floorPt.z.toFixed(2) });
          } else if (onSelectObject) {
            onSelectObject(null);
          }
        }
      }

      dragRef.current.active = false;
      dragRef.current.object = null;
      orbit.enabled = true;
    };

    renderer.domElement.addEventListener("pointerdown", onPointerDown);
    renderer.domElement.addEventListener("pointermove", onPointerMove);
    renderer.domElement.addEventListener("pointerup", onPointerUp);

    let animId;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      orbit.update();
      renderer.render(scene, camera);
    };
    animate();

    const resizeObs = new ResizeObserver(() => {
      const w = container.clientWidth, h = container.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    });
    resizeObs.observe(container);

    threeRef.current = { renderer, scene, camera, orbit };

    // Always set up capture
    window.__sceneBuilderCapture = () => {
      renderer.render(scene, camera);
      const dataUrl = renderer.domElement.toDataURL("image/png");
      return dataUrl.split(",")[1];
    };

    return () => {
      cancelAnimationFrame(animId);
      resizeObs.disconnect();
      renderer.domElement.removeEventListener("pointerdown", onPointerDown);
      renderer.domElement.removeEventListener("pointermove", onPointerMove);
      renderer.domElement.removeEventListener("pointerup", onPointerUp);
      orbit.dispose();
      renderer.dispose();
      delete window.__sceneBuilderCapture;
      if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Rebuild room ---
  useEffect(() => {
    const three = threeRef.current;
    if (!three) return;
    const { scene } = three;

    const oldRoom = scene.getObjectByName("room");
    if (oldRoom) { oldRoom.traverse(c => { if (c.geometry) c.geometry.dispose(); if (c.material) c.material.dispose(); }); scene.remove(oldRoom); }

    const room = layout?.room || { width: 8, depth: 8, height: 3, wall_color: "#e8e0d0", floor_color: "#8b7355" };
    const roomGroup = new THREE.Group(); roomGroup.name = "room";
    const w = room.width || 8, d = room.depth || 8, h = room.height || 3;

    // Floor
    const floorGeo = new THREE.PlaneGeometry(w, d);
    const floorMat = new THREE.MeshStandardMaterial({ color: room.floor_color || "#8b7355", roughness: 0.9 });
    const floor = new THREE.Mesh(floorGeo, floorMat);
    floor.rotation.x = -Math.PI / 2; floor.receiveShadow = true;
    roomGroup.add(floor);

    // Walls
    const wallMat = new THREE.MeshStandardMaterial({ color: room.wall_color || "#e8e0d0", roughness: 0.8, side: THREE.DoubleSide });
    const backWall = new THREE.Mesh(new THREE.PlaneGeometry(w, h), wallMat);
    backWall.position.set(0, h / 2, -d / 2); backWall.receiveShadow = true; roomGroup.add(backWall);
    const leftWall = new THREE.Mesh(new THREE.PlaneGeometry(d, h), wallMat.clone());
    leftWall.position.set(-w / 2, h / 2, 0); leftWall.rotation.y = Math.PI / 2; roomGroup.add(leftWall);
    const rightWall = new THREE.Mesh(new THREE.PlaneGeometry(d, h), wallMat.clone());
    rightWall.position.set(w / 2, h / 2, 0); rightWall.rotation.y = -Math.PI / 2; roomGroup.add(rightWall);

    // Grid
    const grid = new THREE.GridHelper(Math.max(w, d), Math.max(w, d) * 2, 0x444444, 0x333333);
    grid.position.y = 0.002; roomGroup.add(grid);

    scene.add(roomGroup);
  }, [layout?.room]);

  // --- Rebuild props ---
  useEffect(() => {
    const group = propsGroupRef.current;
    if (!group) return;
    while (group.children.length) {
      const child = group.children[0];
      child.traverse(c => { if (c.geometry) c.geometry.dispose(); if (c.material) { if (c.material.map) c.material.map.dispose(); c.material.dispose(); } });
      group.remove(child);
    }
    (layout?.props || []).forEach(prop => group.add(createPropMesh(prop)));
  }, [layout?.props]);

  // --- Rebuild characters ---
  useEffect(() => {
    const group = charsGroupRef.current;
    if (!group) return;
    while (group.children.length) {
      const child = group.children[0];
      child.traverse(c => { if (c.geometry) c.geometry.dispose(); if (c.material) { if (c.material.map) c.material.map.dispose(); c.material.dispose(); } });
      group.remove(child);
    }
    (layout?.characters || []).forEach((char, i) => group.add(createCharacterMesh(char, i)));
  }, [layout?.characters]);

  // --- Rebuild lighting ---
  useEffect(() => {
    const three = threeRef.current;
    if (!three) return;
    const { scene } = three;

    ["ambient-light", "key-light", "fill-light", "back-light"].forEach(name => {
      const old = scene.getObjectByName(name);
      if (old) scene.remove(old);
    });

    const lighting = layout?.lighting || {};
    const ambient = new THREE.AmbientLight(lighting.ambient?.color || "#ffffff", lighting.ambient?.intensity ?? 0.4);
    ambient.name = "ambient-light"; scene.add(ambient);

    const addSpot = (name, cfg, defaults) => {
      const pos = cfg?.position || defaults.position;
      const light = new THREE.SpotLight(cfg?.color || defaults.color, cfg?.intensity ?? defaults.intensity, 30, Math.PI / 4, 0.5, 1);
      light.position.set(pos.x, pos.y, pos.z);
      light.castShadow = true; light.shadow.mapSize.set(1024, 1024);
      light.name = name; light.target.position.set(0, 0.5, 0);
      scene.add(light); scene.add(light.target);
    };
    addSpot("key-light", lighting.key, { position: { x: 3, y: 4, z: 2 }, intensity: 1.0, color: "#fff5e0" });
    addSpot("fill-light", lighting.fill, { position: { x: -3, y: 2, z: 2 }, intensity: 0.5, color: "#e0e8ff" });
    addSpot("back-light", lighting.back, { position: { x: 0, y: 3, z: -3 }, intensity: 0.3, color: "#ffffff" });
  }, [layout?.lighting]);

  // --- Highlight selected object ---
  useEffect(() => {
    const propsGroup = propsGroupRef.current;
    const charsGroup = charsGroupRef.current;
    if (!propsGroup || !charsGroup) return;

    const all = [...propsGroup.children, ...charsGroup.children];
    all.forEach(obj => {
      const isSelected = obj.userData?.id === selectedId;
      obj.traverse(c => {
        if (c.isMesh && c.material) {
          c.material.emissive = c.material.emissive || new THREE.Color(0);
          c.material.emissiveIntensity = isSelected ? 0.3 : 0;
          if (isSelected) c.material.emissive.set(0xd4a84b);
        }
      });
    });
  }, [selectedId, layout?.props, layout?.characters]);

  return (
    <div
      ref={containerRef}
      className="w-full h-full"
      style={{ background: "#1a1a2e", cursor: "grab" }}
      data-testid="scene-builder-3d"
    />
  );
}
